import React, { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { resolveLandingPath } from "@/app/auth/resolveNavAccess";
import { useNavContext } from "@/app/auth/useNavContext";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { FullScreenLoader } from "@/shared/components/FullScreenLoader";

interface PublicRoutesProps {
  children: ReactNode;
  to?: string;
}

export const PublicRoute: React.FC<PublicRoutesProps> = ({ children, to = "/app" }) => {
  const { isAuthenticated, isInitialized } = useAuth();
  const navContext = useNavContext();
  const location = useLocation();

  // Landing comes from the navigation registry, so an authenticated user always arrives somewhere they
  // can actually open. The previous role-keyed table ignored `user_type` and sent ANY `operator` to
  // `/app/news` — the INTERNAL news page — so an organization member invited as an operator landed on
  // a screen that was not theirs. `null` means nothing is visible; `to` then applies and the dashboard's
  // own fallback handles it.
  const landingPath = (navContext && resolveLandingPath(navContext)) ?? to;

  // Gate on isInitialized, never isLoading — isLoading also toggles during login
  // submission and would unmount the login form mid-flow (broke 2FA login).
  if (!isInitialized) {
    return <FullScreenLoader />;
  }

  // Only an in-app path is honoured, so the login can never be turned into an open redirect.
  const from = (location.state as { from?: unknown } | null)?.from;
  const destination = typeof from === "string" && from.startsWith("/app/") ? from : landingPath;

  return !isAuthenticated ? <>{children}</> : <Navigate to={destination} replace />;
};
